import UrlHelper from '@/util/Url'
import VerofreshHttpAdapter from '@/orm/VeroFreshHttpAdapter'
import JwtDecode from 'jwt-decode'
import Moment from 'moment'
import Cookie from 'js-cookie'
import store from '@/store'
import router from '@/router'
import axios from 'axios'

export default {
  /**
   * Return whether there is currently a JWT available for an authorization header
   * @param state
   * @return {boolean}
   */
  hasAuthorizationHeader() {
    return Cookie.get('jwt') != null
  },

  /**
   * Creates an authorization header formatted the way our API wants
   *
   * @param {*} state the state object on this module
   * @return String
   */
  getAuthorizationHeader() {
    return `Bearer ${Cookie.get('jwt')}`
  },

  /**
   * Creates an authorization header formatted the way our API wants
   *
   * @param {*} state the state object on this module
   * @return String
   */
  getJwt() {
    return Cookie.get('jwt')
  },

  /**
   * Inspects the JWT and determines if the token is within the nbf (not
   * before) and exp (expired time) limits
   *
   * @param {*} state the state object on this module
   * @return Boolean
   */
  jwtTimeIsValid() {
    try {
      const jwt = Cookie.get('jwt')
      const token = JwtDecode(jwt)
      const expiresAfterNow = Moment.unix(token.exp).isAfter(Moment())

      // Add an hour to the current time when checking not before to allow for
      // a little clock drift
      const notBeforeTime = Moment.unix(token.nbf).isBefore(Moment().add(1, 'hour'))

      return expiresAfterNow && notBeforeTime
    } catch (error) {
      // If we couldn't parse the jwt or times associated then return false to
      // force the client to refresh the jwt
      return false
    }
  },

  /**
   * Get the time the current JWT expires in seconds
   *
   * @param Boolean if true returns a time relative to now
   * @return Integer
   */
  jwtExpiration(relative = false) {
    try {
      const token = JwtDecode(this.getJwt())
      if (relative) {
        return token.exp - Moment().unix()
      } else {
        return token.exp
      }
    } catch (error) {
      // If we couldn't parse the jwt or times associated then return false to
      // force the client to refresh the jwt
      return false
    }
  },

  /**
   * Set the JSON Web Token.
   * @param jwt String Json web token
   */
  setJwt(jwt) {
    // If the user identified by the new JWT is different then the user
    // currently in the store, reset the user so that the app reloads the user
    // record from the remote API
    const user = store.getters['userModule/getUser']
    if (user.id !== undefined && JwtDecode(jwt).uid !== user.id) {
      store.dispatch('userModule/resetUser', null, { root: true })
    }

    // Domain is set to parent domain (e.g., .verofresh.com) to allow subdomains (e.g., chatbot) to read it.
    const cookieDomain = UrlHelper.getCookieDomain()

    // Remove any legacy cookie without domain set (migration from old behavior)
    Cookie.remove('jwt')

    if (store.getters['userModule/keepLoggedIn']) {
      // Update the JWT cookie with an expiration 1 day from now,
      // about the same amount of time as the updated JWT itself.
      Cookie.set('jwt', jwt, { expires: 1, secure: true, domain: cookieDomain })
    } else {
      // Update session JWT cookie
      Cookie.set('jwt', jwt, { secure: true, domain: cookieDomain })
    }
  },

  /**
   * Perform an auth request
   * @param email
   * @param password
   */
  auth(email, password) {
    const requestConfig = {
      auth: {
        username: email,
        password: password
      }
    }
    return axios.get(`${UrlHelper.getApiUrl()}/auth`, requestConfig)
      .then(response => {
        const jwt = this.parseJwt(response)
        this.setJwt(jwt)
      })
  },

  /**
   * Get the JWT from a response's headers
   * @param response
   * @return {string | *}
   */
  parseJwt(response) {
    try {
      return response.headers.authorization.substring(7)
    } catch (e) {
      return null
    }
  },

  /**
   * Validates if a URL is a valid VeroFresh external URL.
   * Only allows https:// URLs on verofresh.com or *.verofresh.com domains.
   * @param url The URL to validate
   * @return {boolean} True if valid external VeroFresh URL
   */
  isValidExternalReturnUrl(url) {
    try {
      const parsed = new URL(url)
      return parsed.protocol === 'https:' &&
             (parsed.hostname === 'verofresh.com' ||
              parsed.hostname.endsWith('.verofresh.com'))
    } catch {
      return false
    }
  },

  /**
   * Checks if a returnUrl is an external (absolute) URL vs internal route.
   * @param url The URL to check
   * @return {boolean} True if URL starts with http:// or https://
   */
  isExternalUrl(url) {
    return url && (url.startsWith('http://') || url.startsWith('https://'))
  },

  /**
   * Log in using a JWT.
   * @param jwt
   * @param keepLoggedIn  Whether to keep the user logged in.
   */
  login(jwt, keepLoggedIn = false) {
    store.commit('userModule/setKeepLoggedIn', keepLoggedIn)
    this.setJwt(jwt)
    if (store.getters['userModule/hasReturnUrl']) {
      const returnUrl = store.getters['userModule/returnUrl']
      store.dispatch('userModule/clearReturnUrl')

      // Check if this is an external URL
      if (this.isExternalUrl(returnUrl)) {
        // Validate external URLs - must be *.verofresh.com
        if (this.isValidExternalReturnUrl(returnUrl)) {
          window.location.href = returnUrl
          return
        }
        // Invalid external URL - fall through to home
      } else {
        // Internal route - use router.push
        router.push(returnUrl)
        return
      }
    }
    router.push('/')
  },

  /**
   * Logout from the app by deleting the JWT cookie and resetting the user
   */
  logout() {
    // Remove cookie from both the exact hostname and parent domain
    Cookie.remove('jwt')
    Cookie.remove('jwt', { domain: UrlHelper.getCookieDomain() })
    if (store.getters['userModule/storedJWT']) {
      this.restoreStoredJWT()
    } else {
      store.dispatch('userModule/resetUser', null, { root: true })
    }
  },

  /**
   * Reset a password
   * @param password
   * @param token
   */
  resetPassword(password, token) {
    const formData = new FormData()
    formData.append('password', password)
    formData.append('token', token)

    return VerofreshHttpAdapter({
      url: `${UrlHelper.getApiUrl()}/auth/reset-password`,
      method: 'POST',
      data: formData
    })
  },

  /**
   * Impersonate a user by retrieving a valid JWT for the specified user id.
   * @param userId
   * @returns {AxiosPromise}
   */
  impersonateUser(userId) {
    // Store the old JWT
    store.commit('userModule/setStoredJWT', this.getJwt())
    VerofreshHttpAdapter({
      url: `${UrlHelper.getApiUrl()}/auth/impersonate-user/`,
      method: 'GET',
      params: {
        userId: userId
      }
    }).then(resp => {
      // Reset the customer context since impersonated user is likely to not have access to the same
      // customers as the logged in user.
      store.dispatch('userModule/resetCustomerContext', null, { root: true })
      // Use the impersonated user's JWT
      this.setJwt(resp.data.jwt)
      // Page must be reloaded for new user context to be properly initialized
      // (Probably not, but having trouble getting it to work with a router.push.)
      window.location.reload()
    })
  },

  /**
   * Restore the stored JWT (restores a user's original JWT from before they impersonated another user.)
   */
  restoreStoredJWT() {
    this.setJwt(store.getters['userModule/storedJWT'])
    store.commit('userModule/setStoredJWT', '')
    // Page must be reloaded for new user context to be properly initialized
    window.location.reload()
  }
}
